/**
 * Language-aware content for Sister Signal.
 *
 * Long-form trauma-informed copy (library entries, sanctuary education,
 * before-you-go-back prompts, financial freedom tips, hotlines).
 *
 * NOTE TO REVIEWERS:
 *   The Spanish translations below were drafted by Claude and tuned for a
 *   neutral Latin American (broadly es-MX) register: warm, feminine,
 *   feminine-tu (tú, not usted), unhurried, never clinical. Anything marked
 *   `// review pending` is flagged for human trauma-informed review before
 *   wide release.
 *
 * Adding a new language:
 *   1. Add a top-level <code> block below with the same shape as `en`/`es`.
 *   2. Import nothing else — this file is consumed by `getContent(lang)`.
 */

const en = {
  LIBRARY: [
    {
      slug: "cycle-of-abuse",
      section: "patterns",
      title: "The Cycle of Abuse",
      short: "Many abusive relationships move through four predictable phases. Naming the cycle helps you see what's coming.",
      feels: "Calm followed by mounting dread, an explosion, then apologies and warmth — and then the dread builds again.",
      why_confusing: "Because the calm and the love-bombing feel like proof things will change. They are part of the pattern, not an exit from it.",
      phases: [
        { name: "Tension building", body: "Stress rises. Communication shrinks. You walk on eggshells. Small things start to feel like threats. Your body is bracing." },
        { name: "Incident", body: "The eruption — verbal, emotional, physical, sexual, coercive, threatening. Could be loud or icy-silent. The pattern is harm." },
        { name: "Reconciliation / honeymoon", body: "Apologies, gifts, promises to change, intense affection, love bombing. You're told ‘this won't happen again’ — and it feels real." },
        { name: "Calm", body: "Temporary stability. Hope returns. You wonder if the worst is behind you. Tension begins again, often invisibly at first." },
      ],
    },
    {
      slug: "power-control-wheel",
      section: "patterns",
      title: "The Power and Control Wheel",
      short: "Common tactics used to maintain control. Often quiet, often legal, almost always layered.",
      feels: "You can't quite name what's wrong, but your autonomy keeps shrinking.",
      why_confusing: "Because each tactic alone can be brushed off as ‘just a bad day.' The pattern emerges only when you see them together.",
      tactics: [
        { name: "Intimidation", body: "Looks, gestures, raised voice, smashed objects, displayed weapons. Fear that doesn't need a single explicit threat." },
        { name: "Emotional abuse", body: "Put-downs, name-calling, ‘playful' humiliation, criticism dressed as feedback, making you feel crazy." },
        { name: "Isolation", body: "Limiting where you go, who you see, who you talk to. Sometimes framed as protection or love." },
        { name: "Minimizing, denying, blaming", body: "‘That never happened.' ‘You're too sensitive.' ‘You made me do it.' Refusing to take responsibility." },
        { name: "Coercion and threats", body: "Threats to leave, harm you, harm the children, harm a pet, report you, expose secrets, take everything in court." },
        { name: "Economic / financial abuse", body: "Controlling money, accounts, work, transportation. Sabotaging your job or education." },
        { name: "Using children", body: "Threats around custody, using kids as messengers, undermining your parenting, weaponising visitation." },
        { name: "Entitlement and control", body: "Treating you like a servant, making the big decisions alone, defining the rules, expecting you to perform a role." },
        { name: "Digital abuse", body: "Tracking your phone, demanding passwords, monitoring messages, surveilling you online." },
      ],
    },
    {
      slug: "coercive-control",
      section: "tactics",
      title: "Coercive control",
      short: "A pattern of behaviour designed to make you smaller, more dependent, more afraid to disagree.",
      feels: "Walking on eggshells. Constantly editing yourself. Forgetting what you used to like.",
      why_confusing: "Because it can be quiet. There may be no bruises, only the slow erosion of your own voice.",
    },
    {
      slug: "gaslighting",
      section: "tactics",
      title: "Gaslighting",
      short: "Persistent denial of your perception until you start to doubt your own memory and senses.",
      feels: "‘That never happened.’ ‘You’re too sensitive.’ A growing sense that you can’t trust yourself.",
      why_confusing: "Because the goal is to make reality itself feel unstable.",
    },
    {
      slug: "darvo",
      section: "tactics",
      title: "DARVO",
      short: "Deny, Attack, Reverse Victim & Offender. A predictable response to being held accountable.",
      feels: "You bring up something that hurt you and somehow leave the conversation apologising.",
      why_confusing: "Because by the end of the conversation, the original concern has vanished.",
    },
    {
      slug: "trauma-bonding",
      section: "recovery",
      title: "Trauma bonding",
      short: "An attachment formed through cycles of harm and intermittent kindness.",
      feels: "Missing them most after the worst days. Mistaking relief for love.",
      why_confusing: "Because the brain bonds to the person who provides the comfort — even if they caused the pain.",
    },
    {
      slug: "financial-abuse",
      section: "tactics",
      title: "Financial abuse",
      short: "Control of money, accounts, work, or resources as a way to limit autonomy.",
      feels: "Having to justify every purchase. Not knowing your own account balances.",
      why_confusing: "Because it’s often framed as ‘help’ or ‘being responsible’.",
    },
    {
      slug: "future-faking",
      section: "tactics",
      title: "Future faking",
      short: "Vivid promises about a future that never quite arrives — used to keep you waiting.",
      feels: "Long, intoxicating conversations about ‘someday’. Reality stays the same.",
      why_confusing: "Because the words are beautiful and the timing is always ‘not yet’.",
    },
    {
      slug: "intermittent-reinforcement",
      section: "tactics",
      title: "Intermittent reinforcement",
      short: "Unpredictable warmth interspersed with coldness or cruelty.",
      feels: "Bracing for the swing. Feeling addicted to the good days.",
      why_confusing: "Because the brain over-values rewards that arrive unpredictably.",
    },
    {
      slug: "reactive-abuse",
      section: "tactics",
      title: "Reactive abuse",
      short: "When sustained provocation finally pulls a reaction out of you — and is then used as proof you’re ‘the problem’.",
      feels: "Shame for snapping after months of swallowing.",
      why_confusing: "Because your reaction is real, but the framing erases the context that produced it.",
    },
    {
      slug: "isolation",
      section: "tactics",
      title: "Isolation",
      short: "Subtle or overt narrowing of your friendships, family, and outside support.",
      feels: "Watching friendships fade without quite remembering when.",
      why_confusing: "Because each individual moment feels small.",
    },
    {
      slug: "stalking",
      section: "tactics",
      title: "Stalking",
      short: "Repeated, unwanted attention or surveillance that causes you to feel watched, monitored, or unsafe.",
      feels: "Drive-bys. Burner accounts. Unsolicited ‘knowing’ of your day.",
      why_confusing: "Because it often follows separation, framed as ‘caring’.",
    },
    {
      slug: "sexual-coercion",
      section: "tactics",
      title: "Sexual coercion",
      short: "Pressure, guilt, or persistence used to gain intimacy you did not freely choose.",
      feels: "Going along to avoid conflict. Feeling far from your own body afterward.",
      why_confusing: "Because it’s often paired with affection or familiarity.",
    },
  ],

  BEFORE_YOU_GO_BACK: [
    { q: "What changed?", help: "Name the specific behaviours that are different now, with evidence." },
    { q: "Do I feel safer — or just lonelier?", help: "Loneliness is real. It is not, on its own, a reason to return." },
    { q: "What happened last time we reconciled?", help: "Look at the pattern, not the promise." },
    { q: "Has accountability actually occurred?", help: "Words are not amends. Sustained changed behaviour is." },
    { q: "Would I want this for someone I love?", help: "Apply your own standard of love to yourself." },
  ],

  SANCTUARY: {
    breathing: [
      { id: "box", name: "Box breath", description: "Inhale 4 · hold 4 · exhale 4 · hold 4.", cycles: 4 },
      { id: "long_exhale", name: "Long exhale", description: "Inhale 4 · exhale 8. Calms the vagus nerve.", cycles: 6 },
      { id: "sigh", name: "Sigh of relief", description: "Two short inhales, one long exhale.", cycles: 6 },
    ],
    grounding: [
      "Five things you can see.",
      "Four things you can touch.",
      "Three things you can hear.",
      "Two things you can smell.",
      "One thing you can taste.",
    ],
    body: [
      "Unclench your jaw. Drop your shoulders. Soften your tongue.",
      "Place a hand on your chest and one on your belly. Slow the breath.",
      "Stand up. Press your feet into the floor for ten seconds.",
      "Drink half a glass of water, slowly.",
    ],
    education: [
      {
        title: "Cortisol overload",
        subtitle: "Why your body feels exhausted but unable to rest",
        body: "Chronic stress keeps cortisol elevated for long periods of time. Over time, this can disrupt sleep, digestion, hormones, concentration, memory, and immune function.",
        bullets: [
          "waking up already tired",
          "anxiety at night but exhaustion during the day",
          "feeling ‘wired and dead’ at the same time",
          "increased inflammation or illness",
          "crashing after finally becoming safe",
        ],
        closer: "Your body is not failing. It may be adapting to prolonged survival stress.",
      },
      {
        title: "Hypervigilance",
        subtitle: "Why you can’t relax even when nothing is wrong",
        body: "A nervous system exposed to unpredictability can become trained to constantly scan for danger.",
        bullets: [
          "waiting for the next conflict",
          "overanalyzing tone changes",
          "panic when someone is quiet",
          "jumping at sounds or footsteps",
          "difficulty relaxing around other people",
          "exhaustion after social interaction",
        ],
        closer: "Hypervigilance is not attention-seeking. It is a survival adaptation.",
      },
      {
        title: "Freeze & dissociation",
        subtitle: "Why your brain goes blank during conflict",
        body: "When stress becomes overwhelming, some nervous systems stop fighting and shift into freeze.",
        bullets: [
          "forgetting words mid-conversation",
          "feeling numb or detached",
          "struggling to defend yourself",
          "agreeing just to end conflict",
          "delayed emotional reactions hours later",
        ],
        closer: "Freeze responses are automatic survival responses — not weakness.",
      },
      {
        title: "ADHD / PTSD overlap",
        subtitle: "When survival stress consumes mental bandwidth",
        body: "Chronic stress and trauma can mimic or intensify ADHD symptoms.",
        bullets: [
          "forgetfulness",
          "losing objects",
          "task paralysis",
          "emotional flooding",
          "sensory overwhelm",
          "difficulty starting simple tasks",
        ],
        closer: "Sometimes the brain is overloaded — not broken.",
      },
      {
        title: "Autoimmune flares",
        subtitle: "When stress begins showing up in the body",
        body: "Long-term stress can affect inflammation, immune function, pain levels, digestion, skin, and fatigue.",
        bullets: [
          "flare-ups during conflict",
          "worsening symptoms after emotional stress",
          "stomach pain or headaches",
          "fatigue around unsafe people",
          "body tension and exhaustion",
        ],
        closer: "The body often keeps score long after the mind tries to minimize what happened.",
      },
      {
        title: "Sleep recovery",
        subtitle: "Why your body crashes after safety returns",
        body: "Many women expect themselves to feel instantly better after leaving stress or conflict. Instead, the body often collapses into exhaustion.",
        bullets: [
          "sleeping more than usual",
          "vivid dreams",
          "brain fog",
          "difficulty waking up",
          "fatigue after finally feeling safe",
        ],
        closer: "Rest is not laziness. Sometimes exhaustion is the body realizing it no longer has to stay on guard.",
      },
    ],
    cycle: [
      {
        title: "Luteal phase & emotional sensitivity",
        subtitle: "Why everything suddenly feels heavier before your period",
        body: "In the luteal phase, many women experience increased emotional sensitivity, lower stress tolerance, fatigue, irritability, grief resurfacing, or heightened awareness of relational imbalance.",
        bullets: [
          "crying more easily",
          "less patience for conflict",
          "feeling emotionally done",
          "stronger reactions to criticism",
          "needing more rest or solitude",
        ],
        closer: "You are not irrational or dramatic. Your nervous system may simply have less bandwidth to suppress what hurts.",
      },
      {
        title: "Ovulation / fertile window",
        subtitle: "Why you suddenly miss them or crave closeness",
        body: "During ovulation and the fertile window, estrogen and dopamine often rise. Many women feel more social, romantic, forgiving, sensual, or emotionally open during this phase.",
        bullets: [
          "wanting reassurance or affection",
          "stronger attraction and sexual desire",
          "idealizing past relationships",
          "hoping reconciliation could work",
          "craving touch or emotional closeness",
        ],
        closer: "Missing someone during ovulation does not erase why you left.",
      },
      {
        title: "Perimenopause & menopause",
        subtitle: "When hormonal change reshapes the nervous system",
        body: "Hormonal shifts can affect mood, sleep, memory, sensory sensitivity, stress tolerance, and emotional regulation.",
        bullets: [
          "anxiety or panic",
          "rage or irritability",
          "brain fog",
          "insomnia",
          "feeling unlike yourself",
        ],
        closer: "You are not too much. Your nervous system may be navigating a major hormonal transition.",
      },
    ],
  },

  HOTLINES: [
    { name: "National Domestic Violence Hotline (US)", phone: "1-800-799-7233", sms: "Text START to 88788", web: "https://www.thehotline.org" },
    { name: "Crisis Text Line", phone: "Text HOME to 741741", web: "https://www.crisistextline.org" },
    { name: "RAINN — Sexual Assault Hotline", phone: "1-800-656-4673", web: "https://www.rainn.org" },
    { name: "StrongHearts Native Helpline", phone: "1-844-762-8483", web: "https://strongheartshelpline.org" },
    { name: "Love Is Respect (dating)", phone: "1-866-331-9474", web: "https://www.loveisrespect.org" },
  ],

  FINANCIAL: [
    { title: "Quietly build an emergency fund", body: "Start with $20 in an account he doesn’t know about. Then $50. Then more. Privacy is power." },
    { title: "Pull your credit report", body: "Annualcreditreport.com (US) is free. Look for accounts you didn’t open." },
    { title: "Document your assets", body: "Photos of titles, deeds, statements, IDs. Save copies somewhere safe outside the home." },
    { title: "Remote-work income ideas", body: "Virtual assistance, transcription, bookkeeping, customer support, online tutoring." },
    { title: "Apartment starter list", body: "Bedding, towels, basic pots, soap, lamp, fan. You don’t need a perfect home to begin." },
    { title: "Free legal help", body: "Search ‘DV legal aid’ + your state. Many family-law clinics offer free consultations." },
  ],
};

// review pending — full trauma-informed Spanish review before wide release.
// Tone: neutral Latin American Spanish (es-MX leaning), warm, feminine, "tú",
// unhurried. No clinical or legalistic phrasing.
const es = {
  LIBRARY: [
    {
      slug: "cycle-of-abuse",
      section: "patterns",
      title: "El ciclo del abuso",
      short: "Muchas relaciones abusivas se mueven en cuatro fases predecibles. Nombrar el ciclo ayuda a ver qué viene.",
      feels: "Calma seguida de un miedo creciente, una explosión, luego disculpas y calidez — y de nuevo el miedo regresa.",
      why_confusing: "Porque la calma y el amor-bombardeo se sienten como prueba de que las cosas cambiarán. Son parte del patrón, no una salida.",
      phases: [
        { name: "Acumulación de tensión", body: "Sube el estrés. La comunicación se encoge. Caminas sobre cáscaras de huevo. Las cosas pequeñas empiezan a sentirse como amenazas." },
        { name: "Incidente", body: "La explosión — verbal, emocional, física, sexual, coercitiva, amenazante. Puede ser ruidosa o silenciosamente helada. El patrón es el daño." },
        { name: "Reconciliación / luna de miel", body: "Disculpas, regalos, promesas de cambio, afecto intenso, amor-bombardeo. Te dicen ‘esto no volverá a pasar' — y suena verdad." },
        { name: "Calma", body: "Estabilidad temporal. La esperanza regresa. Te preguntas si lo peor ya pasó. La tensión empieza otra vez, a menudo invisible al principio." },
      ],
    },
    {
      slug: "power-control-wheel",
      section: "patterns",
      title: "La rueda de poder y control",
      short: "Tácticas comunes para mantener el control. A menudo silenciosas, a menudo legales, casi siempre superpuestas.",
      feels: "No puedes nombrar bien qué está mal, pero tu autonomía se va encogiendo.",
      why_confusing: "Porque cada táctica por separado se puede minimizar como ‘un mal día.' El patrón aparece solo cuando las ves juntas.",
      tactics: [
        { name: "Intimidación", body: "Miradas, gestos, voz alta, objetos rotos, armas a la vista. Miedo que no necesita una amenaza explícita." },
        { name: "Abuso emocional", body: "Insultos, apodos, humillación ‘en broma,' crítica disfrazada de retroalimentación, hacerte sentir que estás loca." },
        { name: "Aislamiento", body: "Limitar a dónde vas, a quién ves, con quién hablas. A veces vendido como protección o amor." },
        { name: "Minimizar, negar, culpar", body: "‘Eso nunca pasó.' ‘Eres demasiado sensible.' ‘Me hiciste hacerlo.' Negarse a responsabilizarse." },
        { name: "Coerción y amenazas", body: "Amenazas de irse, lastimarte, lastimar a los niños o a una mascota, denunciarte, exponer secretos, quitarte todo en la corte." },
        { name: "Abuso económico / financiero", body: "Controlar el dinero, las cuentas, el trabajo, el transporte. Sabotear tu empleo o estudios." },
        { name: "Usar a los niños", body: "Amenazas sobre la custodia, usar a los niños como mensajeros, socavar tu crianza, manipular las visitas." },
        { name: "Privilegio y control", body: "Tratarte como sirvienta, tomar solo las decisiones grandes, definir las reglas, esperar que cumplas un rol." },
        { name: "Abuso digital", body: "Rastrear tu celular, exigir contraseñas, vigilar mensajes, espiarte en línea." },
      ],
    },
    {
      slug: "coercive-control",
      section: "tactics",
      title: "Control coercitivo",
      short: "Un patrón de conducta pensado para hacerte sentir más pequeña, más dependiente, con más miedo de discrepar.",
      feels: "Caminar sobre cáscaras de huevo. Editarte constantemente. Olvidar qué te gustaba antes.",
      why_confusing: "Porque puede ser silencioso. Quizá no haya marcas — solo la lenta erosión de tu propia voz.",
    },
    {
      slug: "gaslighting",
      section: "tactics",
      title: "Gaslighting (luz de gas)",
      short: "Negación persistente de tu percepción hasta que empiezas a dudar de tu propia memoria y de tus sentidos.",
      feels: "‘Eso nunca pasó.’ ‘Eres demasiado sensible.’ Una sensación creciente de no poder confiar en ti.",
      why_confusing: "Porque el objetivo es que la realidad misma se sienta inestable.",
    },
    {
      slug: "darvo",
      section: "tactics",
      title: "DARVO",
      short: "Negar, Atacar, Invertir Víctima y Ofensor. Una respuesta predecible cuando se le pide cuentas a alguien.",
      feels: "Mencionas algo que te dolió y, de algún modo, terminas la conversación pidiendo perdón.",
      why_confusing: "Porque al final de la conversación, la preocupación original desapareció.",
    },
    {
      slug: "trauma-bonding",
      section: "recovery",
      title: "Vínculo traumático",
      short: "Un apego formado a través de ciclos de daño y bondad intermitente.",
      feels: "Extrañarle más después de los peores días. Confundir el alivio con amor.",
      why_confusing: "Porque el cerebro se apega a quien provee el consuelo — incluso si esa misma persona causó el dolor.",
    },
    {
      slug: "financial-abuse",
      section: "tactics",
      title: "Abuso económico",
      short: "Control del dinero, las cuentas, el trabajo o los recursos como forma de limitar tu autonomía.",
      feels: "Tener que justificar cada compra. No conocer los saldos de tus propias cuentas.",
      why_confusing: "Porque suele presentarse como ‘ayuda’ o como ‘ser responsables’.",
    },
    {
      slug: "future-faking",
      section: "tactics",
      title: "Falso futuro",
      short: "Promesas vívidas sobre un futuro que nunca termina de llegar — usadas para mantenerte esperando.",
      feels: "Conversaciones largas y embriagantes sobre ‘algún día’. La realidad no cambia.",
      why_confusing: "Porque las palabras son hermosas y el tiempo siempre es ‘todavía no’.",
    },
    {
      slug: "intermittent-reinforcement",
      section: "tactics",
      title: "Refuerzo intermitente",
      short: "Calidez impredecible mezclada con frialdad o crueldad.",
      feels: "Vivir tensa, esperando el siguiente cambio. Sentirte enganchada a los días buenos.",
      why_confusing: "Porque el cerebro le da un valor exagerado a las recompensas que llegan de forma impredecible.",
    },
    {
      slug: "reactive-abuse",
      section: "tactics",
      title: "Abuso reactivo",
      short: "Cuando la provocación sostenida finalmente te saca una reacción — y luego se usa como prueba de que tú eres ‘el problema’.",
      feels: "Vergüenza por estallar después de meses de tragarte todo.",
      why_confusing: "Porque tu reacción es real, pero el encuadre borra el contexto que la produjo.",
    },
    {
      slug: "isolation",
      section: "tactics",
      title: "Aislamiento",
      short: "Un cierre sutil o explícito de tus amistades, tu familia y tus apoyos externos.",
      feels: "Ver cómo las amistades se desvanecen sin recordar exactamente cuándo.",
      why_confusing: "Porque cada momento, por separado, se siente pequeño.",
    },
    {
      slug: "stalking",
      section: "tactics",
      title: "Acoso (stalking)",
      short: "Atención o vigilancia repetida y no deseada que te hace sentir observada, monitoreada o insegura.",
      feels: "Pasadas en auto. Cuentas falsas. Un ‘saber’ no pedido sobre tu día.",
      why_confusing: "Porque suele aparecer después de la separación, disfrazado de ‘cuidado’.",
    },
    {
      slug: "sexual-coercion",
      section: "tactics",
      title: "Coerción sexual",
      short: "Presión, culpa o insistencia usadas para obtener una intimidad que no elegiste con libertad.",
      feels: "Acceder para evitar el conflicto. Sentirte lejos de tu propio cuerpo después.",
      why_confusing: "Porque a menudo viene acompañada de afecto o familiaridad.",
    },
  ],

  BEFORE_YOU_GO_BACK: [
    { q: "¿Qué cambió?", help: "Nombra las conductas específicas que son diferentes ahora, con evidencia." },
    { q: "¿Me siento más segura — o solo más sola?", help: "La soledad es real. Por sí sola, no es razón para volver." },
    { q: "¿Qué pasó la última vez que nos reconciliamos?", help: "Mira el patrón, no la promesa." },
    { q: "¿Realmente hubo asunción de responsabilidad?", help: "Las palabras no reparan. La conducta sostenida y distinta, sí." },
    { q: "¿Querría esto para alguien que amo?", help: "Aplícate tu propio estándar de amor." },
  ],

  SANCTUARY: {
    breathing: [
      { id: "box", name: "Respiración en caja", description: "Inhala 4 · sostén 4 · exhala 4 · sostén 4.", cycles: 4 },
      { id: "long_exhale", name: "Exhalación larga", description: "Inhala 4 · exhala 8. Calma el nervio vago.", cycles: 6 },
      { id: "sigh", name: "Suspiro de alivio", description: "Dos inhalaciones cortas, una exhalación larga.", cycles: 6 },
    ],
    grounding: [
      "Cinco cosas que puedes ver.",
      "Cuatro cosas que puedes tocar.",
      "Tres cosas que puedes escuchar.",
      "Dos cosas que puedes oler.",
      "Una cosa que puedes saborear.",
    ],
    body: [
      "Suelta la mandíbula. Baja los hombros. Suaviza la lengua.",
      "Pon una mano en el pecho y otra en el vientre. Haz la respiración más lenta.",
      "Ponte de pie. Presiona los pies contra el suelo por diez segundos.",
      "Bebe medio vaso de agua, despacio.",
    ],
    education: [
      {
        title: "Sobrecarga de cortisol",
        subtitle: "Por qué tu cuerpo se siente agotado pero no puede descansar",
        body: "El estrés crónico mantiene el cortisol elevado durante largos periodos. Con el tiempo, esto puede alterar el sueño, la digestión, las hormonas, la concentración, la memoria y la función inmune.",
        bullets: [
          "despertar ya cansada",
          "ansiedad por la noche y agotamiento durante el día",
          "sentirte ‘encendida y apagada’ al mismo tiempo",
          "más inflamación o enfermedades",
          "desplomarte justo cuando por fin estás a salvo",
        ],
        closer: "Tu cuerpo no está fallando. Quizá se está adaptando a un estrés de supervivencia prolongado.",
      },
      {
        title: "Hipervigilancia",
        subtitle: "Por qué no puedes relajarte aunque nada esté pasando",
        body: "Un sistema nervioso expuesto a lo impredecible puede aprender a buscar peligro de forma constante.",
        bullets: [
          "esperar el próximo conflicto",
          "sobreanalizar los cambios de tono",
          "entrar en pánico cuando alguien se queda en silencio",
          "saltar con ruidos o pasos",
          "costarte relajarte cerca de otras personas",
          "quedar agotada después de socializar",
        ],
        closer: "La hipervigilancia no es buscar atención. Es una adaptación de supervivencia.",
      },
      {
        title: "Congelamiento y disociación",
        subtitle: "Por qué tu mente se pone en blanco durante un conflicto",
        body: "Cuando el estrés se vuelve abrumador, algunos sistemas nerviosos dejan de pelear y entran en congelamiento.",
        bullets: [
          "olvidar palabras en medio de una conversación",
          "sentirte adormecida o desconectada",
          "costarte defenderte",
          "ceder solo para que el conflicto termine",
          "reacciones emocionales que llegan horas después",
        ],
        closer: "El congelamiento es una respuesta automática de supervivencia — no es debilidad.",
      },
      {
        title: "TDAH / TEPT — superposición",
        subtitle: "Cuando el estrés de supervivencia se traga tu ancho de banda mental",
        body: "El estrés crónico y el trauma pueden imitar o intensificar síntomas parecidos al TDAH.",
        bullets: [
          "olvidos",
          "perder objetos",
          "parálisis ante las tareas",
          "desbordamiento emocional",
          "saturación sensorial",
          "dificultad para empezar tareas simples",
        ],
        closer: "A veces el cerebro está sobrecargado — no roto.",
      },
      {
        title: "Brotes autoinmunes",
        subtitle: "Cuando el estrés empieza a aparecer en el cuerpo",
        body: "El estrés prolongado puede afectar la inflamación, el sistema inmune, el dolor, la digestión, la piel y la energía.",
        bullets: [
          "brotes durante los conflictos",
          "síntomas que empeoran después del estrés emocional",
          "dolor de estómago o de cabeza",
          "fatiga cerca de personas que no son seguras",
          "tensión y cansancio en el cuerpo",
        ],
        closer: "El cuerpo suele llevar la cuenta mucho después de que la mente intenta minimizar lo ocurrido.",
      },
      {
        title: "Recuperar el sueño",
        subtitle: "Por qué tu cuerpo se desploma cuando regresa la seguridad",
        body: "Muchas mujeres esperan sentirse bien de inmediato después de salir del estrés o el conflicto. En cambio, el cuerpo suele colapsar en agotamiento.",
        bullets: [
          "dormir más de lo habitual",
          "sueños vívidos",
          "niebla mental",
          "costarte despertarte",
          "fatiga justo cuando por fin estás a salvo",
        ],
        closer: "Descansar no es pereza. A veces el agotamiento es el cuerpo dándose cuenta de que ya no tiene que estar en guardia.",
      },
    ],
    cycle: [
      {
        title: "Fase lútea y sensibilidad emocional",
        subtitle: "Por qué todo se siente más pesado antes de tu periodo",
        body: "En la fase lútea, muchas mujeres experimentan mayor sensibilidad emocional, menos tolerancia al estrés, fatiga, irritabilidad, duelo que vuelve a aparecer, o más conciencia del desequilibrio en las relaciones.",
        bullets: [
          "llorar con más facilidad",
          "menos paciencia ante el conflicto",
          "sentirte emocionalmente acabada",
          "reaccionar más fuerte a la crítica",
          "necesitar más descanso o soledad",
        ],
        closer: "No estás siendo irracional ni dramática. Tu sistema nervioso simplemente tiene menos espacio para suprimir lo que duele.",
      },
      {
        title: "Ovulación / ventana fértil",
        subtitle: "Por qué de pronto los extrañas o anhelas cercanía",
        body: "Durante la ovulación y la ventana fértil, el estrógeno y la dopamina suelen subir. Muchas mujeres se sienten más sociales, románticas, indulgentes, sensuales o emocionalmente abiertas en esta fase.",
        bullets: [
          "querer afecto o seguridad",
          "más atracción y deseo sexual",
          "idealizar relaciones pasadas",
          "esperar que reconciliarse podría funcionar",
          "anhelar contacto o cercanía emocional",
        ],
        closer: "Extrañar a alguien durante la ovulación no borra las razones por las que te fuiste.",
      },
      {
        title: "Perimenopausia y menopausia",
        subtitle: "Cuando el cambio hormonal rediseña el sistema nervioso",
        body: "Los cambios hormonales pueden afectar el ánimo, el sueño, la memoria, la sensibilidad sensorial, la tolerancia al estrés y la regulación emocional.",
        bullets: [
          "ansiedad o pánico",
          "rabia o irritabilidad",
          "niebla mental",
          "insomnio",
          "sentirte distinta a ti misma",
        ],
        closer: "No eres demasiado. Tu sistema nervioso puede estar atravesando una transición hormonal grande.",
      },
    ],
  },

  // We keep US hotlines because most beta users are based in the US; we add
  // bilingual indicator notes where the service is known to support Spanish.
  HOTLINES: [
    { name: "Línea Nacional contra la Violencia Doméstica (EE. UU., bilingüe)", phone: "1-800-799-7233", sms: "Envía START al 88788", web: "https://www.thehotline.org" },
    { name: "Crisis Text Line (bilingüe disponible)", phone: "Envía AYUDA al 741741", web: "https://www.crisistextline.org" },
    { name: "RAINN — Línea de agresión sexual (bilingüe)", phone: "1-800-656-4673", web: "https://www.rainn.org" },
    { name: "StrongHearts Native Helpline", phone: "1-844-762-8483", web: "https://strongheartshelpline.org" },
    { name: "Love Is Respect (relaciones, bilingüe)", phone: "1-866-331-9474", web: "https://www.loveisrespect.org" },
  ],

  FINANCIAL: [
    { title: "Construye un fondo de emergencia, en silencio", body: "Empieza con $20 en una cuenta que él no conozca. Luego $50. Luego más. La privacidad es poder." },
    { title: "Revisa tu reporte de crédito", body: "Annualcreditreport.com (EE. UU.) es gratuito. Busca cuentas que tú no abriste." },
    { title: "Documenta tus bienes", body: "Fotos de títulos, escrituras, estados de cuenta, identificaciones. Guarda copias en un lugar seguro fuera de la casa." },
    { title: "Ideas de ingreso remoto", body: "Asistencia virtual, transcripción, contabilidad básica, atención al cliente, tutorías en línea." },
    { title: "Lista para un nuevo departamento", body: "Sábanas, toallas, ollas básicas, jabón, lámpara, ventilador. No necesitas una casa perfecta para empezar." },
    { title: "Apoyo legal gratuito", body: "Busca ‘ayuda legal violencia doméstica’ + tu estado o ciudad. Muchas clínicas familiares ofrecen consultas gratis." },
  ],
};

const REGISTRY = { en, es };

/** Returns the content bundle for the requested language; falls back to English. */
export function getContent(lang) {
  return REGISTRY[lang] || REGISTRY.en;
}

// Backward-compatible English exports (any old import that did
// `import { LIBRARY } from '../data/content'` still works). For new code,
// prefer `useContent()` from hooks/useContent.js so the page re-renders
// when the user switches languages.
export const LIBRARY = en.LIBRARY;
export const BEFORE_YOU_GO_BACK = en.BEFORE_YOU_GO_BACK;
export const SANCTUARY = en.SANCTUARY;
export const HOTLINES = en.HOTLINES;
export const FINANCIAL = en.FINANCIAL;
